import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import VideoBanner from './VideoBanner';

const API = process.env.REACT_APP_BACKEND_URL;

const Icon = ({ children, size = 21 }) => (
  <svg
    width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.4"
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
  >
    {children}
  </svg>
);
const BackIcon = () => (<Icon size={18}><path d="M15 5l-7 7 7 7" /></Icon>);
const DownloadIcon = () => (<Icon size={17}><path d="M12 4v11M8 11l4 4 4-4M5 20h14" /></Icon>);
const TrashIcon = () => (<Icon size={17}><path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" /></Icon>);
const PlusIcon = () => (<Icon size={16}><path d="M12 5v14M5 12h14" /></Icon>);
const InfoIcon = () => (<Icon size={17}><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></Icon>);
const CopyIcon = () => (<Icon size={15}><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></Icon>);
const RegenIcon = () => (<Icon size={15}><path d="M4 12a8 8 0 0 1 14-5.3L20 8M20 4v4h-4M20 12a8 8 0 0 1-14 5.3L4 16M4 20v-4h4" /></Icon>);

const usd = (n) => `$${Number(n || 0).toFixed(3)}`;
const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); }
  catch { return ''; }
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return '—'; }
};

export default function VideoLibrary() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [videos, setVideos] = useState([]);
  const [totalSpent, setTotalSpent] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);   // which card's details are open
  const [copiedId, setCopiedId] = useState(null);       // brief "copied" feedback
  const [modelLabels, setModelLabels] = useState({});   // slug -> friendly label

  useEffect(() => {
    // grab the friendly model names so details show "Seedance 2.0" not the slug
    axios.get(`${API}/video/models?token=${token}`)
      .then(res => {
        const map = {};
        (res.data.models || []).forEach(m => { map[m.id] = m.label; });
        setModelLabels(map);
      })
      .catch(() => {});
  }, [token]);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/video/library?token=${token}`);
      setVideos(res.data.videos || []);
      setTotalSpent(res.data.total_spent || 0);
    } catch (e) {
      console.log(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // light auto-refresh so anything still generating flips to done on its own
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, []);

  const remove = async (id) => {
    if (!window.confirm('delete this video for good?')) return;
    setVideos(prev => prev.filter(v => v.id !== id)); // optimistic
    try { await axios.delete(`${API}/video/${id}?token=${token}`); }
    catch (e) { console.log(e); load(); }
  };

  // Cloudinary trick: inserting fl_attachment makes it serve the file as a
  // real download (Content-Disposition: attachment) instead of a video that
  // just plays inline — this is what makes "save to files" appear on mobile.
  const forceDownloadUrl = (url, name) => {
    if (!url || !url.includes('/upload/')) return url;
    const safe = (name || 'video')
      .replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').slice(0, 40).toLowerCase() || 'video';
    return url.replace('/upload/', `/upload/fl_attachment:${safe}/`);
  };

  const download = (v) => {
    const url = forceDownloadUrl(v.video_url, v.prompt);
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.click();
  };

  const copyPrompt = async (v) => {
    try {
      await navigator.clipboard.writeText(v.prompt || '');
      setCopiedId(v.id);
      setTimeout(() => setCopiedId(c => (c === v.id ? null : c)), 1800);
    } catch (e) {
      console.log(e);
    }
  };

  // stash this clip's exact settings, then jump to the generate page —
  // VideoHome reads 'videoRegenerate' from localStorage on mount and prefills.
  const regenerate = (v) => {
    const payload = {
      prompt: v.prompt,
      model: v.model,
      resolution: v.resolution,
      aspect_ratio: v.aspect_ratio,
      duration: v.duration,
      generate_audio: v.generate_audio,
      first_frame_url: v.first_frame_url || null,
      last_frame_url: v.last_frame_url || null,
      input_reference_urls: v.input_reference_urls || [],
    };
    localStorage.setItem('videoRegenerate', JSON.stringify(payload));
    navigate('/video');
  };

  const s = {
    page: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: '#0e0b0f', fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      color: '#e8ddd0', display: 'flex', flexDirection: 'column', overflowY: 'hidden',
    },
    header: {
      padding: '20px 28px', display: 'flex', alignItems: 'center', gap: '14px',
      borderBottom: '1px solid rgba(201,147,90,0.06)',
      background: 'linear-gradient(180deg, #120f18 0%, #0e0b0f 100%)', flexShrink: 0, zIndex: 50,
    },
    backBtn: {
      width: '34px', height: '34px', borderRadius: '50%', background: 'transparent',
      border: '1px solid rgba(201,147,90,0.2)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#c9935a', flexShrink: 0,
    },
    logo: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '22px', fontWeight: 300,
      fontStyle: 'italic', color: '#e8ddd0', letterSpacing: '0.04em', flex: 1,
    },
    logoSpan: { color: '#c9935a' },
    newBtn: {
      padding: '8px 14px', borderRadius: '20px', border: '1px solid rgba(201,147,90,0.2)',
      background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
      display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0,
    },
    body: {
      flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: '32px 24px 80px', maxWidth: '680px', width: '100%', margin: '0 auto', boxSizing: 'border-box',
    },
    greeting: { marginBottom: '24px' },
    greetingTop: { fontSize: '13px', color: '#4a3d30', letterSpacing: '0.06em', marginBottom: '4px' },
    greetingName: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '30px',
      fontWeight: 300, fontStyle: 'italic', color: '#e8ddd0',
    },
    spendStrip: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 18px', borderRadius: '14px',
      background: 'linear-gradient(135deg, rgba(58,42,26,0.4), rgba(90,61,32,0.25))',
      border: '1px solid rgba(201,147,90,0.18)', marginBottom: '26px',
    },
    spendLabel: { fontSize: '11px', letterSpacing: '0.1em', color: '#8a7a6a', textTransform: 'uppercase' },
    spendValue: {
      fontFamily: "'Cormorant Garamond', serif", fontSize: '24px', fontStyle: 'italic', color: '#c9935a',
    },
    card: {
      background: '#1e1825', border: '1px solid rgba(201,147,90,0.15)',
      borderRadius: '18px', overflow: 'hidden', marginBottom: '16px',
    },
    video: { width: '100%', display: 'block', background: '#0e0b0f' },
    pending: {
      width: '100%', aspectRatio: '16 / 9', display: 'flex', alignItems: 'center',
      justifyContent: 'center', color: '#8a7a6a', fontSize: '13px', background: '#0e0b0f',
    },
    cardBody: { padding: '14px 16px 16px' },
    prompt: { fontSize: '13.5px', color: '#e8ddd0', lineHeight: 1.5, marginBottom: '10px' },
    metaRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' },
    meta: { fontSize: '11px', color: '#4a3d30' },
    cost: { fontSize: '13px', color: '#c9935a' },
    actions: { display: 'flex', gap: '8px', marginTop: '12px' },
    actionBtn: {
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
      padding: '9px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.15)',
      background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    deleteBtn: { color: '#8a7a6a', border: '1px solid rgba(201,147,90,0.08)' },
    infoBtn: (open) => ({
      flex: '0 0 auto', width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '9px', borderRadius: '12px', cursor: 'pointer',
      border: open ? '1px solid rgba(201,147,90,0.32)' : '1px solid rgba(201,147,90,0.15)',
      background: open ? 'rgba(201,147,90,0.08)' : 'transparent', color: '#c9935a',
    }),

    /* details panel */
    details: {
      marginTop: '14px', paddingTop: '14px',
      borderTop: '1px solid rgba(201,147,90,0.08)',
    },
    detailPromptLabel: {
      fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase',
      color: '#4a3d30', marginBottom: '6px',
    },
    detailPrompt: {
      fontSize: '13px', color: '#e8ddd0', lineHeight: 1.6, marginBottom: '14px',
      background: '#0e0b0f', border: '1px solid rgba(201,147,90,0.1)',
      borderRadius: '12px', padding: '12px 14px',
    },
    specGrid: {
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px', marginBottom: '14px',
    },
    specItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
    specKey: { fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#4a3d30' },
    specVal: { fontSize: '13px', color: '#e8ddd0' },
    refThumbs: { display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' },
    refThumbWrap: { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' },
    refThumb: {
      width: '58px', height: '58px', borderRadius: '10px', objectFit: 'cover',
      border: '1px solid rgba(201,147,90,0.18)',
    },
    refThumbLabel: { fontSize: '9.5px', color: '#4a3d30', letterSpacing: '0.04em' },
    detailActions: { display: 'flex', gap: '8px' },
    detailBtn: {
      flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
      padding: '10px', borderRadius: '12px', border: '1px solid rgba(201,147,90,0.15)',
      background: 'transparent', color: '#c9935a', fontSize: '12px', cursor: 'pointer',
      fontFamily: "'DM Sans', sans-serif", fontWeight: 300,
    },
    emptyState: {
      background: '#1e1825', border: '1px dashed rgba(201,147,90,0.15)',
      borderRadius: '18px', padding: '48px 24px', textAlign: 'center',
    },
    emptyText: { fontSize: '14px', color: '#4a3d30', lineHeight: 1.8 },
  };

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Sans:wght@300;400&display=swap" rel="stylesheet" />

      <header style={s.header}>
        <div style={s.backBtn} onClick={() => navigate('/video')} role="button" tabIndex={0}>
          <BackIcon />
        </div>
        <div style={s.logo}>re<span style={s.logoSpan}>v</span>erie</div>
        <div style={s.newBtn} onClick={() => navigate('/video')} role="button" tabIndex={0}>
          <PlusIcon /> new
        </div>
      </header>

      <div style={s.body}>
        <div style={s.greeting}>
          <div style={s.greetingTop}>your videos</div>
          <div style={s.greetingName}>the library ♡</div>
        </div>

        <div style={s.spendStrip}>
          <div style={s.spendLabel}>total spent</div>
          <div style={s.spendValue}>{usd(totalSpent)}</div>
        </div>

        {loading ? (
          <div style={s.emptyState}><div style={s.emptyText}>loading…</div></div>
        ) : videos.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyText}>no videos yet 🌙<br />go dream one up</div>
          </div>
        ) : (
          videos.map(v => (
            <div key={v.id} style={s.card}>
              {v.status === 'completed' && v.video_url ? (
                <video src={v.video_url} style={s.video} controls loop playsInline preload="metadata" />
              ) : v.status === 'failed' ? (
                <div style={s.pending}>failed — {v.error || 'something went wrong'}</div>
              ) : (
                <div style={s.pending}>generating… 🌙</div>
              )}

              <div style={s.cardBody}>
                <div style={s.prompt}>{v.prompt}</div>
                <div style={s.metaRow}>
                  <div style={s.meta}>
                    {v.resolution} · {v.aspect_ratio} · {v.duration}s
                    {v.generate_audio ? ' · audio' : ''} · {fmtDate(v.created_at)}
                  </div>
                  {v.status === 'completed' && <div style={s.cost}>{usd(v.cost)}</div>}
                </div>

                <div style={s.actions}>
                  {v.status === 'completed' && v.video_url && (
                    <div style={s.actionBtn} onClick={() => download(v)} role="button" tabIndex={0}>
                      <DownloadIcon /> download
                    </div>
                  )}
                  <div style={{ ...s.actionBtn, ...s.deleteBtn }} onClick={() => remove(v.id)} role="button" tabIndex={0}>
                    <TrashIcon /> delete
                  </div>
                  <div
                    style={s.infoBtn(expandedId === v.id)}
                    onClick={() => setExpandedId(id => (id === v.id ? null : v.id))}
                    role="button" tabIndex={0} aria-label="details"
                  >
                    <InfoIcon />
                  </div>
                </div>

                {expandedId === v.id && (
                  <div style={s.details}>
                    <div style={s.detailPromptLabel}>prompt</div>
                    <div style={s.detailPrompt}>{v.prompt}</div>

                    <div style={s.specGrid}>
                      <div style={s.specItem}>
                        <div style={s.specKey}>model</div>
                        <div style={s.specVal}>{modelLabels[v.model] || v.model}</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>resolution</div>
                        <div style={s.specVal}>{v.resolution}</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>aspect ratio</div>
                        <div style={s.specVal}>{v.aspect_ratio}</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>length</div>
                        <div style={s.specVal}>{v.duration}s</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>audio</div>
                        <div style={s.specVal}>{v.generate_audio ? 'on' : 'off'}</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>seed</div>
                        <div style={s.specVal}>{v.seed != null ? v.seed : 'random'}</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>cost</div>
                        <div style={s.specVal}>{v.status === 'completed' ? usd(v.cost) : '—'}</div>
                      </div>
                      <div style={s.specItem}>
                        <div style={s.specKey}>created</div>
                        <div style={s.specVal}>{fmtDateTime(v.created_at)}</div>
                      </div>
                    </div>

                    {(v.first_frame_url || v.last_frame_url ||
                      (v.input_reference_urls && v.input_reference_urls.length > 0)) && (
                      <>
                        <div style={s.detailPromptLabel}>references</div>
                        <div style={s.refThumbs}>
                          {v.first_frame_url && (
                            <div style={s.refThumbWrap}>
                              <img src={v.first_frame_url} alt="first frame" style={s.refThumb} />
                              <div style={s.refThumbLabel}>first frame</div>
                            </div>
                          )}
                          {v.last_frame_url && (
                            <div style={s.refThumbWrap}>
                              <img src={v.last_frame_url} alt="last frame" style={s.refThumb} />
                              <div style={s.refThumbLabel}>last frame</div>
                            </div>
                          )}
                          {(v.input_reference_urls || []).map((url, i) => (
                            <div key={i} style={s.refThumbWrap}>
                              <img src={url} alt={`ref ${i + 1}`} style={s.refThumb} />
                              <div style={s.refThumbLabel}>ref {i + 1}</div>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    <div style={s.detailActions}>
                      <div style={s.detailBtn} onClick={() => copyPrompt(v)} role="button" tabIndex={0}>
                        <CopyIcon /> {copiedId === v.id ? 'copied ♡' : 'copy prompt'}
                      </div>
                      <div style={s.detailBtn} onClick={() => regenerate(v)} role="button" tabIndex={0}>
                        <RegenIcon /> regenerate
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <VideoBanner />
    </div>
  );
}
